import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { cancelReassessmentRequest, requestGetSiteSpecie } from 'api/siteSpecie'
import { SiteContext } from 'providers/site'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import SpeciesDetailsForm from './components/SpeciesDetailsForm'
import SpeciesDetailsAssessmentForm from './components/SpeciesDetailsAssessmentForm'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import { showErrorToast } from 'utils/toast'
import WarningModal from 'components/Modal/WarningModal'
import { ProfileContext } from 'providers/profile'
import { INDIVIDUAL_ROLE_ID } from 'constants/roles'
import { SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses'
import toNumberOrNull from 'utils/toNumberOrNull'

function deepEqual (obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }
  if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false
  }
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) {
    return false
  }
  for (const key of keys1) {
    if (!deepEqual(obj1[key], obj2[key])) {
      return false
    }
  }
  return true
}

function arraysEqual (arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) {
      return false
    }
  }

  return true
}

const SpeciesDetailsModal = ({ item, isOpen, setIsOpen, closeEditSpeciesModal }) => {
  const { profile } = useContext(ProfileContext)
  const isIndividualUser = profile.Role.id === INDIVIDUAL_ROLE_ID

  const params = useParams()
  const { site, siteProposalFieldValidation, setSiteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const [specie, setSpecie] = useState({ SpeciesAssesmentParameters: [] })
  const [selectedIds, setSelectedIds] = useState([])
  const [isWarningOpen, setIsWarningOpen] = useState(false)

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus
    ? toNumberOrNull(site.sitehistoryId)
    : toNumberOrNull(params.siteHistoryId)

  const specieId = item.reassessmentFlag ? item.historyId : item.id
  const speciesValidation = siteProposalFieldValidation.species[specieId]
  const checkFields = speciesValidation || defaultSiteProposalValidationResults.species

  const [speciesLoadError, setSpeciesLoadError] = useState(false)

  function getSiteSpecie() {
    setSpeciesLoadError(false)
    requestGetSiteSpecie(item.id, historyId, item.historyId).then((data) => {
      if (data?.error || !data?.scientificName) {
        setSpeciesLoadError(true)
        setSpecie({ SpeciesAssesmentParameters: [] })
        return
      }
      setSpecie(data)
      const warningCategories = ['A-NR', 'Q-NR', 'S-NR', 'U-NR', 'U-UR', 'A-UR']

      // Check if taxonTreatment is in the warning categories and show a warning
      if (warningCategories.includes(data.taxonTreatment)) {
        // If taxonTreatment matches the warning categories, show the warning modal
        setIsWarningOpen(true)
      }
    }).catch(() => {
      setSpeciesLoadError(true)
      setSpecie({ SpeciesAssesmentParameters: [] })
    })
  }

  const confirmAction = () => {
    if (!isIndividualUser) {
      showErrorToast('This action is not allowed.')
      return
    }
    cancelReassessmentRequest({
      siteId: historyId,
      specieId: item.historyId,
      isCancelled: true
    }).then((data) => {
      if (data.error) {
        return showErrorToast(data.error)
      }
      setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
      setIsOpen(false)
      closeEditSpeciesModal()
    })
  }

  useEffect(() => {
    getSiteSpecie()
  }, [item.id, item.historyId, historyId])

  const handleUpdateChosenIds = (Ids) => {
    if (selectedIds && Ids) {
      if (!arraysEqual(selectedIds, Ids)) {
        setSelectedIds(Ids)
      }
    }
  }

  return (
    <div>
      <Modal
        width='416px'
        isOpen={isOpen}
        toggleModal={setIsOpen}
        modalTitleId='cancelReassessment'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsOpen(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='returnDiscardChangesText' values={{ specieName: <b>{item.scientificName}</b> }} />}
          item={item}
          approve={true}
        />
      </Modal>

      <WarningModal
        width='416px'
        isOpen={isWarningOpen}
        toggleModal={() => {
          setIsWarningOpen((open) => !open)
        }}
        modalTitleId='warningModalHeader'
        selectedIds={selectedIds}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsWarningOpen(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='warningModalExplanation' values={{ specieName: <b>{item.scientificName}</b> }} />}
          item={item}
          approve={true}
        />
      </WarningModal>

      {speciesLoadError && (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <FormattedMessage id="speciesNotFoundOrNoAccess" defaultMessage="Species not found or you do not have access to view this species." />
        </div>
      )}
      {specie.scientificName && !speciesLoadError && (
        <>
          <SpeciesDetailsForm
            item={specie}
            checkFields={checkFields}
            site={site}
            getSiteSpecie={getSiteSpecie}
            isEditable={isEditable}
          />
          <SpeciesDetailsAssessmentForm
            item={specie}
            getSiteSpecie={getSiteSpecie}
            specie={specie}
            setSpecie={setSpecie}
            setItem={setSpecie}
            checkFields={checkFields}
            site={site}
            setSiteProposalFieldValidation={setSiteProposalFieldValidation}
            isEditable={isEditable}
            updateIds={handleUpdateChosenIds}
          />
        </>
      )}
    </div>
  )
}

SpeciesDetailsModal.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    historyId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool,
    scientificName: PropTypes.string
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  closeEditSpeciesModal: PropTypes.func.isRequired
}

export default SpeciesDetailsModal
